import React, { useCallback, useMemo } from 'react';
import {
  UserIcon,
  CalendarDaysIcon,
  ScaleIcon,
  DocumentTextIcon,
  BuildingLibraryIcon,
  CurrencyDollarIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import type { Entity as BackendEntity, EntityAction } from '@/features/chat/types/entity';

// Entity type definitions
export type EntityType = 'client' | 'date' | 'legal_ref' | 'document' | 'court' | 'money' | 'matter' | 'unknown';

export interface Entity {
  type: string;
  value: string;
  originalText: string;
  startIndex: number;
  endIndex: number;
  confidence?: number;
  metadata?: Record<string, any>;
  actions?: EntityAction[];
  source?: 'backend' | 'regex';
}

export interface EntityStyleConfig {
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  hoverColor: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

// Color-coded entity styling configurations
const ENTITY_CONFIGS: Record<EntityType, EntityStyleConfig> = {
  client: {
    backgroundColor: '#2563EB',
    textColor: '#FFFFFF',
    borderColor: '#1D4ED8',
    hoverColor: '#1E40AF',
    icon: UserIcon,
    description: 'Client or party'
  },
  matter: {
    backgroundColor: '#0EA5E9',
    textColor: '#FFFFFF',
    borderColor: '#0284C7',
    hoverColor: '#0369A1',
    icon: BriefcaseIcon,
    description: 'Matter or case'
  },
  date: {
    backgroundColor: '#10B981',
    textColor: '#FFFFFF',
    borderColor: '#059669',
    hoverColor: '#047857',
    icon: CalendarDaysIcon,
    description: 'Date or time'
  },
  legal_ref: {
    backgroundColor: '#9333EA',
    textColor: '#FFFFFF',
    borderColor: '#7C3AED',
    hoverColor: '#6B21A8',
    icon: ScaleIcon,
    description: 'Legal reference or statute'
  },
  document: {
    backgroundColor: '#F59E0B',
    textColor: '#FFFFFF',
    borderColor: '#D97706',
    hoverColor: '#B45309',
    icon: DocumentTextIcon,
    description: 'Document or filing'
  },
  court: {
    backgroundColor: '#DC2626',
    textColor: '#FFFFFF',
    borderColor: '#B91C1C',
    hoverColor: '#991B1B',
    icon: BuildingLibraryIcon,
    description: 'Court or jurisdiction'
  },
  money: {
    backgroundColor: '#059669',
    textColor: '#FFFFFF',
    borderColor: '#047857',
    hoverColor: '#065F46',
    icon: CurrencyDollarIcon,
    description: 'Money or billing'
  },
  unknown: {
    backgroundColor: '#6B7280',
    textColor: '#FFFFFF',
    borderColor: '#4B5563',
    hoverColor: '#374151',
    icon: DocumentTextIcon,
    description: 'Entity'
  }
};

const ENTITY_TYPE_ALIASES: Record<string, EntityType> = {
  client: 'client',
  contact: 'client',
  person: 'client',
  party: 'client',
  matter: 'matter',
  case: 'matter',
  date: 'date',
  time: 'date',
  legal_ref: 'legal_ref',
  statute: 'legal_ref',
  regulation: 'legal_ref',
  document: 'document',
  filing: 'document',
  court: 'court',
  money: 'money',
  amount: 'money',
  billing: 'money',
};

const normalizeEntityType = (rawType: string): EntityType => {
  const key = rawType?.toLowerCase?.() || '';
  return ENTITY_TYPE_ALIASES[key] || 'unknown';
};

const getEntityConfig = (rawType: string): EntityStyleConfig => {
  const normalized = normalizeEntityType(rawType);
  return ENTITY_CONFIGS[normalized] || ENTITY_CONFIGS.unknown;
};

// Entity detection patterns
type RegexEntityType = Exclude<EntityType, 'matter' | 'unknown'>;
const ENTITY_PATTERNS: Record<RegexEntityType, RegExp[]> = {
  client: [
    /\b([A-Z][a-z]+ [A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\b/g, // Names like "John Smith" or "Maria Gonzalez"
    /\b([A-Z][a-z]+\s+(?:LLC|Corp|Inc|Co|Ltd|Corporation|Company|Partnership|LLP|LP))\b/gi, // Company names
    /\b(plaintiff|defendant|petitioner|respondent|appellant|appellee)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/gi
  ],
  date: [
    /\b(\d{1,2}\/\d{1,2}\/\d{2,4})\b/g, // Date formats like 12/31/2024
    /\b(\d{1,2}-\d{1,2}-\d{2,4})\b/g, // Date formats like 12-31-2024
    /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}/gi,
    /\b(next|last|this)\s+(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|week|month|year)/gi,
    /\b(tomorrow|yesterday|today)\b/gi,
    /\b(\d{1,2}:\d{2}\s*(?:AM|PM|am|pm)?)\b/g // Time formats
  ],
  legal_ref: [
    /\b([A-Z][a-z]+\s+(?:Code|Rules?|Statutes?|Regulations?|Act)(?:\s+of\s+[A-Z][a-z]+)?)\s+(?:§|Section|Rule|Art\.|Article)\s*([\d.-]+[A-Za-z]*)/gi,
    /\b(Fed\.?\s*R\.?\s*(?:Civ\.?\s*P\.?|Crim\.?\s*P\.?|Evid\.?))\s*(\d+(?:\([a-z]\))?)/gi, // Federal Rules
    /\b(U\.?S\.?C\.?)\s*(?:§|Section)\s*(\d+(?:-\d+)?)/gi, // U.S. Code
    /\b(\d+\s+U\.?S\.?\s+\d+)/gi, // Supreme Court citations
    /\b(\d+\s+F\.?\d*d?\s+\d+)/gi // Federal court citations
  ],
  document: [
    /\b(Motion\s+(?:for|to)\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/gi,
    /\b(Brief|Memorandum|Pleading|Complaint|Answer|Petition|Response|Reply|Declaration|Affidavit|Deposition|Transcript|Contract|Agreement|Order|Judgment|Decree|Subpoena|Summons|Notice|Discovery|Interrogatories|Request(?:s)?\s+for\s+(?:Production|Admissions?))/gi,
    /\b([A-Z][a-z]+\s+(?:Agreement|Contract|Lease|Will|Trust|Deed|Mortgage|License|Permit|Certificate|Report|Analysis|Summary|Review))/gi
  ],
  court: [
    /\b(Supreme\s+Court(?:\s+of\s+[A-Z][a-z]+)?)/gi,
    /\b((?:Superior|District|Circuit|Appellate|Family|Probate|Criminal|Civil|Municipal|Justice|Magistrate|Bankruptcy)\s+Court(?:\s+of\s+[A-Z][a-z]+)?)/gi,
    /\b(\d+(?:st|nd|rd|th)\s+(?:Circuit|District)(?:\s+Court)?)/gi,
    /\b(Court\s+of\s+(?:Appeals?|Common\s+Pleas)(?:\s+of\s+[A-Z][a-z]+)?)/gi,
    /\b([A-Z][a-z]+\s+(?:County|Parish)\s+(?:Superior|District|Circuit|Family|Probate)\s+Court)/gi
  ],
  money: [
    /\$[\d,]+(?:\.\d{2})?/g, // Dollar amounts
    /\b(billing\s+rate|hourly\s+rate|retainer|fee|cost|expense|damages|settlement|judgment|award|fine|penalty)/gi,
    /\b(\d+(?:,\d{3})*(?:\.\d{2})?\s+dollars?)/gi,
    /\b(attorney(?:s'?)?\s+fees?|court\s+costs?|legal\s+fees?)/gi
  ]
};

interface EntitySpanProps {
  entity: Entity;
  onClick?: (entity: Entity) => void;
  onHover?: (entity: Entity) => void;
  children: React.ReactNode;
  compact?: boolean;
}

const EntitySpan: React.FC<EntitySpanProps> = ({ 
  entity, 
  onClick, 
  onHover, 
  children, 
  compact = false 
}) => {
  const config = getEntityConfig(entity.type);
  const IconComponent = config.icon;

  const handleClick = useCallback(() => {
    onClick?.(entity);
  }, [onClick, entity]);

  const handleMouseEnter = useCallback(() => {
    onHover?.(entity);
  }, [onHover, entity]);

  const paddingClass = compact ? 'px-1.5 py-0.5' : 'px-2 py-1';
  const textSize = compact ? 'text-xs' : 'text-sm';

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-md font-medium cursor-pointer
        transition-all duration-200 hover:scale-[1.02] hover:shadow-sm
        ${paddingClass} ${textSize}
      `}
      style={{
        backgroundColor: config.backgroundColor,
        color: config.textColor,
        border: `1px solid ${config.borderColor}`,
      }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      title={`${config.description}: ${entity.value}`}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = config.hoverColor;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = config.backgroundColor;
      }}
    >
      <IconComponent className="w-3 h-3 flex-shrink-0" />
      {children}
    </span>
  );
};

interface EntityRendererProps {
  text: string;
  entities?: BackendEntity[];
  onEntityClick?: (entity: Entity) => void;
  onEntityHover?: (entity: Entity) => void;
  compact?: boolean;
  className?: string;
}

export const EntityRenderer: React.FC<EntityRendererProps> = ({
  text,
  entities: backendEntities,
  onEntityClick,
  onEntityHover,
  compact = false,
  className = ''
}) => {
  const normalizeBackendEntities = useCallback((entities: BackendEntity[], messageText: string): Entity[] => {
    if (!entities || entities.length === 0) return [];
    return entities
      .filter(entity =>
        typeof entity.start === 'number' &&
        typeof entity.end === 'number' &&
        entity.start >= 0 &&
        entity.end <= messageText.length &&
        entity.start < entity.end
      )
      .map(entity => {
        const startIndex = entity.start;
        const endIndex = entity.end;
        const originalText = messageText.slice(startIndex, endIndex);
        const normalizedType = normalizeEntityType(entity.type);
        return {
          type: normalizedType,
          value: entity.value || originalText,
          originalText: originalText || entity.value,
          startIndex,
          endIndex,
          confidence: entity.metadata?.confidence ?? 1,
          metadata: entity.metadata,
          actions: entity.actions,
          source: 'backend',
        };
      });
  }, []);

  const detectRegexEntities = useCallback((messageText: string): Entity[] => {
    const detectedEntities: Entity[] = [];
    
    // Process each entity type
    Object.entries(ENTITY_PATTERNS).forEach(([entityType, patterns]) => {
      patterns.forEach((pattern) => {
        const matches = Array.from(messageText.matchAll(pattern));
        matches.forEach((match) => {
          if (match.index !== undefined) {
            detectedEntities.push({
              type: entityType,
              value: match[1] || match[0],
              originalText: match[0],
              startIndex: match.index,
              endIndex: match.index + match[0].length,
              confidence: 0.8, // Default confidence
              source: 'regex',
            });
          }
        });
      });
    });

    // Sort by start index and remove overlaps
    detectedEntities.sort((a, b) => a.startIndex - b.startIndex);
    
    const filteredEntities: Entity[] = [];
    let lastEndIndex = 0;
    
    detectedEntities.forEach((entity) => {
      if (entity.startIndex >= lastEndIndex) {
        filteredEntities.push(entity);
        lastEndIndex = entity.endIndex;
      }
    });

    return filteredEntities;
  }, []);

  const entities = useMemo(() => {
    if (backendEntities && backendEntities.length > 0) {
      const normalized = normalizeBackendEntities(backendEntities, text);
      if (normalized.length > 0) return normalized;
    }
    return detectRegexEntities(text);
  }, [backendEntities, text, normalizeBackendEntities, detectRegexEntities]);

  // Render text with entity highlights
  const renderTextWithEntities = useCallback(() => {
    if (entities.length === 0) {
      return <span className={className}>{text}</span>;
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    entities.forEach((entity, index) => {
      // Add text before entity
      if (entity.startIndex > lastIndex) {
        parts.push(
          <span key={`text-${index}`}>
            {text.slice(lastIndex, entity.startIndex)}
          </span>
        );
      }

      // Add entity span
      const displayText = entity.originalText || text.slice(entity.startIndex, entity.endIndex) || entity.value;
      parts.push(
        <EntitySpan
          key={`entity-${index}`}
          entity={entity}
          onClick={onEntityClick}
          onHover={onEntityHover}
          compact={compact}
        >
          {displayText}
        </EntitySpan>
      );

      lastIndex = entity.endIndex;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key="text-end">
          {text.slice(lastIndex)}
        </span>
      );
    }

    return <span className={className}>{parts}</span>;
  }, [text, entities, onEntityClick, onEntityHover, compact, className]);

  return renderTextWithEntities();
};

export default EntityRenderer; 
